import React from "react";
import { BookOpen, Upload, UserCheck, Star, Bookmark, Share2, ExternalLink, BarChart3 } from "lucide-react";
import { FeatureHero, FeatureRow, IconGrid, PhotoBand, ClosingCta, FeatureFooterNav, Shot } from "@/components/features/FeatureKit";
import { ProfileFieldsMock, KpiMock } from "@/components/features/mocks";

const PHOTO = "https://images.unsplash.com/photo-1615914143778-1a1a6e50c5dd?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=1800";

export default function ResourcesFeature() {
  return (
    <div className="overflow-x-hidden" data-testid="feature-resources-page">
      <FeatureHero
        eyebrow="Resources"
        icon={BookOpen}
        title="Good startup knowledge shouldn't sink in a feed."
        copy="Startup Otter Resources gives useful tools, guides, programs, services, and community knowledge somewhere to live."
        ctaLabel="Explore Resources"
        ctaTo="/resources"
        visual={<Shot src="/features/resource.png" alt="A Startup Otter resource page for Angel Investing 101, with cover image, ratings and share actions" url="startupotter.com/resource/angel-investing-101" testId="resources-hero-visual" priority />}
      />

      <FeatureRow
        testId="resources-publish"
        label="Publish"
        icon={Upload}
        title="Share something useful."
        copy="Publish Resources the startup community can actually use—from tools and guides to programs and opportunities."
        visual={<ProfileFieldsMock title="Resource details" testId="mock-resource-details" items={[["Type", "Video"], ["Topics", "Investing"], ["Industries", "Wealth & Investing"], ["Link", "angelinvesting101.com"]]} />}
      />

      <FeatureRow
        testId="resources-attribution"
        tone="dark"
        flip
        label="Attribution"
        icon={UserCheck}
        title="Know who shared it."
        copy="Resources stay connected to the people and organizations behind them, adding valuable community context."
        visual={<Shot src="/features/resource.png" alt="A resource page crediting the member who shared it" url="startupotter.com/resource/angel-investing-101" dark cropClass="h-[420px]" testId="resources-attribution-shot" />}
      />

      <IconGrid
        testId="resources-actions"
        tone="tint"
        columns={4}
        title="Save it, pass it along, or go straight to the source."
        intro="Every Resource comes with the four actions people actually want."
        items={[
          { icon: Star, title: "Rate", copy: "Ratings help members understand which Resources others have found valuable." },
          { icon: Bookmark, title: "Bookmark", copy: "Bookmark useful Resources instead of losing another great link somewhere in your browser." },
          { icon: Share2, title: "Share", copy: "Copy a Resource link or share it directly with your Startup Otter friends." },
          { icon: ExternalLink, title: "Visit", copy: "Visit external Resources when you're ready to learn, apply, register, purchase, or take the next step." },
        ]}
      />

      <PhotoBand
        testId="resources-learning"
        src={PHOTO}
        alt="Someone taking notes beside a laptop"
        kicker="The library"
        title="Findable long after it was posted."
        copy="Resources live in a browsable, searchable library instead of scrolling away in a feed."
      />

      <FeatureRow
        testId="resources-discovery"
        label="Browse"
        icon={BookOpen}
        title="The whole library, filtered to what you need."
        copy="Filter by type, topic and industry, and see what the community has rated highest."
        visual={<Shot src="/features/resources-directory.png" alt="The Startup Otter resource library with community ratings and filters" url="startupotter.com/resources" testId="resources-directory-shot" />}
      />

      <FeatureRow
        testId="resources-analytics"
        tone="tint"
        flip
        label="Analytics"
        icon={BarChart3}
        title="See what resonates."
        copy="Resource Analytics help publishers understand views, visits, shares, ratings, and engagement."
        visual={<KpiMock testId="mock-resource-kpis" items={[{ label: "Resource visits", value: "1,032", delta: "+16%" }, { label: "Visit clicks", value: "214", delta: "+12%" }, { label: "Shares", value: "48", delta: "+7" }, { label: "Avg rating", value: "4.8", delta: "+0.2" }]} />}
      />

      <ClosingCta
        testId="resources-closing"
        title="Less disappearing knowledge. More useful stuff."
        ctaLabel="Explore Resources"
        ctaTo="/resources"
      />

      <FeatureFooterNav next={{ to: "/features/otter-assist", label: "Otter Assist", desc: "Tell us what you need and let Otter Assist surface people who can help." }} />
    </div>
  );
}
